import * as React from "react"
import { Link, navigate } from "gatsby"
import { useLocation } from "@gatsbyjs/reach-router"
import { Activity, AudioLines, Braces, Layers3, TextAlignStart } from "lucide-react"
import { mountNavGlassFilter } from "../utils/nav-glass-filter"
import { useMotionPreference } from "../utils/motion-preference"

const navItems = [
  { to: "/", label: "Home", icon: TextAlignStart },
  { to: "/music", label: "Music", icon: AudioLines },
  { to: "/activities", label: "Activity", icon: Activity },
  { to: "/memories", label: "Memories", icon: Layers3 },
  { to: "/repl", label: "REPL", icon: Braces },
]

const DEBUG_NAVIGATION_NAVIGATE = false

const SNAP_TO_CLOSEST_DURATION_MS = 380
const DRAG_ACTIVATION_TRANSITION_MS = 125

const normalizePath = path => {
  if (!path || path === "/") return "/"
  return path.replace(/\/+$/, "")
}

const indexFromPath = pathname => {
  const normalizedPath = normalizePath(pathname)
  const matchIndex = navItems.findIndex(({ to }) => {
    if (to === "/") return normalizedPath === "/"
    return normalizedPath === to || normalizedPath.startsWith(`${to}/`)
  })
  return matchIndex === -1 ? 0 : matchIndex
}

const clampIndex = index => Math.max(0, Math.min(navItems.length - 1, index))
const clampValue = (value, min, max) => Math.min(Math.max(value, min), max)

const Navigation = () => {
  const location = useLocation()
  const navRef = React.useRef(null)
  const itemRefs = React.useRef([])
  const pointerIdRef = React.useRef(null)
  const suppressClickRef = React.useRef(false)
  const metricsRef = React.useRef(null)
  const snapTimerRef = React.useRef(null)
  // Renamed: now holds a setTimeout id (not rAF id)
  const dragActivationTimerRef = React.useRef(null)
  const dragActivationStartRef = React.useRef(null)
  const [previewIndex, setPreviewIndex] = React.useState(null)
  const [trackerLeft, setTrackerLeft] = React.useState(null)
  const [trackerWidth, setTrackerWidth] = React.useState(null)
  const [trackerState, setTrackerState] = React.useState("idle")
  const [debugCurrentIndex, setDebugCurrentIndex] = React.useState(null)
  const { reduceMotion } = useMotionPreference()

  const routeCurrentIndex = React.useMemo(
    () => indexFromPath(location.pathname),
    [location.pathname]
  )
  const currentIndex =
    DEBUG_NAVIGATION_NAVIGATE ? debugCurrentIndex ?? routeCurrentIndex : routeCurrentIndex
  const visualIndex = previewIndex ?? currentIndex

  const clearSnapTimer = React.useCallback(() => {
    if (snapTimerRef.current !== null) {
      window.clearTimeout(snapTimerRef.current)
      snapTimerRef.current = null
    }
  }, [])

  // ─── KEY CHANGE: use clearTimeout instead of cancelAnimationFrame ───────────
  const clearDragActivationTimer = React.useCallback(() => {
    if (dragActivationTimerRef.current !== null) {
      window.clearTimeout(dragActivationTimerRef.current)
      dragActivationTimerRef.current = null
    }
    dragActivationStartRef.current = null
  }, [])

  // ─── KEY CHANGE: direct DOM mutation instead of rAF polling ─────────────────
  // The tracker element's `left` is already being updated via --nav-tracker-left.
  // We override its transition inline for the activation window, then clear it.
  // This keeps the interpolation on the compositor thread with zero re-renders.
  const startDragActivationTransition = React.useCallback(() => {
    clearDragActivationTimer()

    const tracker = navRef.current?.querySelector(".floating-nav__tracker")
    if (tracker) {
      tracker.style.transition = `left ${DRAG_ACTIVATION_TRANSITION_MS}ms ease`
    }

    dragActivationTimerRef.current = window.setTimeout(() => {
      setTrackerState("tracking")
      if (tracker) tracker.style.transition = ""
      dragActivationTimerRef.current = null
    }, DRAG_ACTIVATION_TRANSITION_MS)
  }, [clearDragActivationTimer])

  const resetTracker = React.useCallback(() => {
    metricsRef.current = null
    setTrackerState("idle")
    clearDragActivationTimer()
  }, [clearDragActivationTimer])

  React.useEffect(
    () => () => {
      clearSnapTimer()
      clearDragActivationTimer()
    },
    [clearDragActivationTimer, clearSnapTimer]
  )

  React.useEffect(() => {
    const navElement = navRef.current
    const rootElement = navElement?.closest(".page-shell")
    const surfaceElement = rootElement?.querySelector("[data-nav-glass-surface]")

    return mountNavGlassFilter({
      navElement,
      surfaceElement,
      rootElement,
      preferNative: reduceMotion,
    })
  }, [reduceMotion])

  const updateSpecular = React.useCallback(event => {
    const navElement = navRef.current
    if (!navElement) return

    const rect = navElement.getBoundingClientRect()
    navElement.style.setProperty("--glass-light-x", `${((event.clientX - rect.left) / rect.width) * 100}%`)
    navElement.style.setProperty("--glass-light-y", `${((event.clientY - rect.top) / rect.height) * 100}%`)
    navElement.style.setProperty("--glass-specular-opacity", "1")
  }, [])

  const clearSpecular = React.useCallback(() => {
    navRef.current?.style.setProperty("--glass-specular-opacity", "0")
  }, [])

  const readLayoutMetrics = React.useCallback(() => {
    const navElement = navRef.current
    if (!navElement) return null
    const itemRects = itemRefs.current.map(item => item?.getBoundingClientRect())
    if (itemRects.some(rect => !rect)) return null
    const navRect = navElement.getBoundingClientRect()
    const leftOffsets = itemRects.map(rect => rect.left - navRect.left)
    const centerPoints = itemRects.map(rect => rect.left + rect.width / 2)
    return {
      navLeft: navRect.left,
      itemWidth: itemRects[0].width,
      leftOffsets,
      centerPoints,
      minLeft: leftOffsets[0],
      maxLeft: leftOffsets[leftOffsets.length - 1],
    }
  }, [])

  const getPreviewFromClientX = React.useCallback((clientX, metrics) => {
    const firstCenter = metrics.centerPoints[0]
    const lastCenter = metrics.centerPoints[metrics.centerPoints.length - 1]
    const clampedCenterX = clampValue(clientX, firstCenter, lastCenter)
    const left = clampValue(
      clampedCenterX - metrics.navLeft - metrics.itemWidth / 2,
      metrics.minLeft,
      metrics.maxLeft
    )
    let nearestIndex = 0
    let nearestDistance = Number.POSITIVE_INFINITY
    metrics.centerPoints.forEach((center, index) => {
      const distance = Math.abs(center - clampedCenterX)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestIndex = index
      }
    })
    return { left, nearestIndex }
  }, [])

  const commitNavigation = React.useCallback(
    targetIndex => {
      const nextIndex = clampIndex(targetIndex)
      if (nextIndex === currentIndex) return
      if (DEBUG_NAVIGATION_NAVIGATE) {
        setDebugCurrentIndex(nextIndex)
        return
      }
      navigate(navItems[nextIndex].to)
    },
    [currentIndex]
  )

  const markClickAsHandled = React.useCallback(() => {
    suppressClickRef.current = true
    window.setTimeout(() => {
      suppressClickRef.current = false
    }, 0)
  }, [])

  const animateToIndex = React.useCallback(
    ({ index, shouldNavigate }) => {
      const metrics = metricsRef.current
      if (!metrics) {
        if (shouldNavigate) commitNavigation(index)
        resetTracker()
        return
      }
      const left = metrics.leftOffsets[index]
      setPreviewIndex(index)
      setTrackerLeft(left)
      setTrackerState("snapping")
      clearSnapTimer()
      snapTimerRef.current = window.setTimeout(() => {
        if (shouldNavigate) commitNavigation(index)
        resetTracker()
      }, SNAP_TO_CLOSEST_DURATION_MS)
    },
    [clearSnapTimer, commitNavigation, resetTracker]
  )

  const gestureStartedOnCurrent = React.useRef(false)
  const pointerWithinCurrentTab = React.useRef(false)
  const DRAG_THRESHOLD = 6
  const dragStartPos = React.useRef(null)
  const dragActivated = React.useRef(false)

  const handlePointerDown = React.useCallback(
    event => {
      if (event.pointerType === "mouse" && event.button !== 0) return
      if (event.pointerType === "mouse") {
        clearSnapTimer()
        clearDragActivationTimer()
        return
      }
      clearSnapTimer()
      clearDragActivationTimer()
      const metrics = readLayoutMetrics()
      if (!metrics) return
      metricsRef.current = metrics
      const preview = getPreviewFromClientX(event.clientX, metrics)
      gestureStartedOnCurrent.current = preview.nearestIndex === currentIndex
      if (gestureStartedOnCurrent.current && itemRefs.current[currentIndex]) {
        const rect = itemRefs.current[currentIndex].getBoundingClientRect()
        pointerWithinCurrentTab.current =
          event.clientX >= rect.left && event.clientX <= rect.right
      } else {
        pointerWithinCurrentTab.current = false
      }
      const initialLeft = metrics.leftOffsets[currentIndex]
      pointerIdRef.current = event.pointerId
      event.currentTarget.setPointerCapture(event.pointerId)
      dragStartPos.current = { x: event.clientX, y: event.clientY }
      dragActivated.current = false
      setPreviewIndex(preview.nearestIndex)
      setTrackerWidth(metrics.itemWidth)
      setTrackerLeft(initialLeft)
      setTrackerState("idle")
      event.preventDefault()
    },
    [clearDragActivationTimer, clearSnapTimer, getPreviewFromClientX, readLayoutMetrics, currentIndex]
  )

  const handleDragStart = React.useCallback(event => {
    event.preventDefault()
  }, [])

  const handlePointerMove = React.useCallback(
    event => {
      updateSpecular(event)
      if (pointerIdRef.current !== event.pointerId) return
      const metrics = metricsRef.current ?? readLayoutMetrics()
      if (!metrics) return
      if (!dragActivated.current && dragStartPos.current) {
        const dx = event.clientX - dragStartPos.current.x
        const dy = event.clientY - dragStartPos.current.y
        if (Math.sqrt(dx * dx + dy * dy) >= DRAG_THRESHOLD) {
          dragActivated.current = true
          setTrackerState("snapping")
          startDragActivationTransition()
        } else {
          return
        }
      }
      let preview = getPreviewFromClientX(event.clientX, metrics)
      if (gestureStartedOnCurrent.current && itemRefs.current[currentIndex]) {
        const rect = itemRefs.current[currentIndex].getBoundingClientRect()
        if (event.clientX >= rect.left && event.clientX <= rect.right) {
          pointerWithinCurrentTab.current = true
          preview = {
            ...preview,
            left: metrics.leftOffsets[currentIndex],
            nearestIndex: currentIndex,
          }
        } else {
          pointerWithinCurrentTab.current = false
        }
      }
      setPreviewIndex(preview.nearestIndex)
      setTrackerLeft(preview.left)
    },
    [getPreviewFromClientX, readLayoutMetrics, currentIndex, startDragActivationTransition, updateSpecular]
  )

  const finishGesture = React.useCallback(
    (event, shouldNavigate) => {
      if (pointerIdRef.current !== event.pointerId) return
      const fallbackMetrics = readLayoutMetrics()
      const metrics = metricsRef.current ?? fallbackMetrics
      pointerIdRef.current = null
      dragStartPos.current = null
      dragActivated.current = false
      clearDragActivationTimer()
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      markClickAsHandled()
      if (!metrics) {
        resetTracker()
        return
      }
      metricsRef.current = metrics
      if (shouldNavigate) {
        const preview = getPreviewFromClientX(event.clientX, metrics)
        animateToIndex({ index: preview.nearestIndex, shouldNavigate: true })
        return
      }
      animateToIndex({ index: currentIndex, shouldNavigate: false })
    },
    [
      animateToIndex,
      currentIndex,
      getPreviewFromClientX,
      markClickAsHandled,
      readLayoutMetrics,
      resetTracker,
      clearDragActivationTimer,
    ]
  )

  const handlePointerUp = React.useCallback(event => finishGesture(event, true), [finishGesture])
  const handlePointerCancel = React.useCallback(
    event => finishGesture(event, false),
    [finishGesture]
  )

  const handleItemClick = React.useCallback(
    (event, targetIndex) => {
      event.preventDefault()
      if (suppressClickRef.current) return
      commitNavigation(targetIndex)
    },
    [commitNavigation]
  )

  const navClasses = [
    "floating-nav",
    trackerState === "tracking" ? "is-tracking" : "",
    trackerState === "snapping" ? "is-snapping" : "",
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <nav className="safe-bottom-nav" aria-label="Primary" data-glass-exclude>
      <div
        ref={navRef}
        className={navClasses}
        style={{
          "--nav-item-count": navItems.length,
          "--nav-active-index": currentIndex,
          "--nav-tracker-left": trackerLeft !== null ? `${trackerLeft}px` : undefined,
          "--nav-tracker-width": trackerWidth !== null ? `${trackerWidth}px` : undefined,
          "--nav-snap-duration": `${SNAP_TO_CLOSEST_DURATION_MS}ms`,
        }}
        onPointerDown={handlePointerDown}
        onPointerEnter={updateSpecular}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={clearSpecular}
        onLostPointerCapture={handlePointerCancel}
      >
        <span className="floating-nav__tracker" aria-hidden="true" />
        {navItems.map(({ to, label, icon: Icon }, index) => (
          <Link
            key={to}
            to={to}
            ref={element => {
              itemRefs.current[index] = element
            }}
            className={`floating-nav__item${index === visualIndex ? " is-preview" : ""}${
              index === currentIndex ? " is-current" : ""
            }`}
            onClick={event => handleItemClick(event, index)}
            onDragStart={handleDragStart}
            draggable={false}
          >
            <Icon className="floating-nav__icon" size={26} strokeWidth={2.5} aria-hidden="true" />
            <span className="floating-nav__label">{label}</span>
          </Link>
        ))}
      </div>
    </nav>
  )
}

export default Navigation
